import { useState, useEffect, useRef } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MarkdownToolbar } from '@/components/MarkdownToolbar';
import { Plus, Pencil, Trash2, Eye } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const emptyEssay = { title: '', excerpt: '', body: '', tags: '', status: 'draft', reading_time: 5, seo_title: '', seo_description: '', scheduled_at: '', substack_url: '' };

export default function AdminEssays() {
  const [essays, setEssays] = useState([]);
  const [form, setForm] = useState(emptyEssay);
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const bodyRef = useRef(null);

  const fetchEssays = () => api.get('/admin/essays').then(res => setEssays(res.data)).catch(console.error);

  useEffect(() => { fetchEssays(); }, []);

  const handleSave = async (overrideStatus) => {
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
        reading_time: parseInt(form.reading_time) || 5
      };
      if (overrideStatus) {
        payload.status = overrideStatus;
      }
      if (editId) {
        await api.put(`/admin/essays/${editId}`, payload);
        toast.success(overrideStatus === 'draft' ? 'Essay saved as draft.' : 'Essay updated.');
      } else {
        await api.post('/admin/essays', payload);
        toast.success(overrideStatus === 'draft' ? 'Draft saved.' : 'Essay created.');
      }
      setDialogOpen(false);
      setForm(emptyEssay);
      setEditId(null);
      fetchEssays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save essay.');
    }
  };

  const handleEdit = (essay) => {
    setEditId(essay._id);
    setForm({
      title: essay.title,
      excerpt: essay.excerpt || '',
      body: essay.body || '',
      tags: essay.tags?.join(', ') || '',
      status: essay.status || 'draft',
      reading_time: essay.reading_time || 5,
      seo_title: essay.seo_title || '',
      seo_description: essay.seo_description || '',
      scheduled_at: essay.scheduled_at ? essay.scheduled_at.slice(0, 16) : '',
      substack_url: essay.substack_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this essay?')) return;
    try {
      await api.delete(`/admin/essays/${id}`);
      toast.success('Essay deleted.');
      fetchEssays();
    } catch (err) {
      toast.error('Failed to delete.');
    }
  };

  const handlePublish = async (essay) => {
    try {
      const newStatus = essay.status === 'published' ? 'draft' : 'published';
      await api.put(`/admin/essays/${essay._id}`, { status: newStatus });
      toast.success(newStatus === 'published' ? 'Essay published.' : 'Essay unpublished.');
      fetchEssays();
    } catch (err) {
      toast.error('Failed to update status.');
    }
  };

  return (
    <AdminShell title="Essays">
      <div className="flex items-center justify-between mb-6">
        <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
          {essays.length} essay{essays.length !== 1 ? 's' : ''}
        </p>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setForm(emptyEssay); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm"
              data-testid="admin-essays-create-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Essay
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
                {editId ? 'Edit Essay' : 'New Essay'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Title</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Excerpt</label>
                <Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} rows={2} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Body (Markdown)</label>
                <MarkdownToolbar
                  textareaRef={bodyRef}
                  value={form.body}
                  onChange={(v) => setForm({...form, body: v})}
                />
                <Textarea ref={bodyRef} value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={12} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm font-mono rounded-t-none border-t-0" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Tags (comma-separated)</label>
                  <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Reading Time (min)</label>
                  <Input type="number" value={form.reading_time} onChange={e => setForm({...form, reading_time: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)]">
                    <SelectItem value="draft" className="text-[hsl(var(--foreground))]">Draft</SelectItem>
                    <SelectItem value="published" className="text-[hsl(var(--foreground))]">Published</SelectItem>
                    <SelectItem value="scheduled" className="text-[hsl(var(--foreground))]">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.status === 'scheduled' && (
                <div>
                  <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Scheduled Publish Date</label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                </div>
              )}
              <div className="border-t border-[rgba(201,168,76,0.1)] pt-4 mt-2">
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-3">SEO Settings</p>
                <div className="space-y-3">
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">SEO Title</label>
                    <Input value={form.seo_title} onChange={e => setForm({...form, seo_title: e.target.value})} placeholder="Custom title for search engines" className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">SEO Description</label>
                    <Textarea value={form.seo_description} onChange={e => setForm({...form, seo_description: e.target.value})} rows={2} placeholder="Custom description for search results" className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm resize-none" />
                  </div>
                  <div>
                    <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Substack URL (optional)</label>
                    <Input value={form.substack_url} onChange={e => setForm({...form, substack_url: e.target.value})} placeholder="https://substack.com/..." className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSave('draft')}
                  variant="outline"
                  className="flex-1 border-[rgba(201,168,76,0.25)] text-[hsl(var(--foreground))] hover:bg-[rgba(27,77,68,0.16)] font-['Inter'] text-sm font-medium py-5 rounded-lg"
                >
                  Save as Draft
                </Button>
                <Button onClick={() => handleSave()} className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg">
                  {editId ? 'Update Essay' : 'Publish Essay'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Title</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Status</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Tags</TableHead>
              <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {essays.map(essay => (
              <TableRow key={essay._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                <TableCell className="font-['Cormorant_Garamond'] text-base font-semibold text-[hsl(var(--foreground))]">
                  {essay.title}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`font-['Inter'] text-[10px] uppercase tracking-[0.12em] cursor-pointer ${
                      essay.status === 'published'
                        ? 'border-[rgba(122,158,126,0.4)] text-[hsl(var(--sage))]'
                        : 'border-[rgba(168,181,178,0.2)] text-[hsl(var(--silver))]'
                    }`}
                    onClick={() => handlePublish(essay)}
                  >
                    {essay.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {essay.tags?.slice(0, 2).map(t => (
                      <Badge key={t} variant="outline" className="font-['Inter'] text-[9px] uppercase border-[rgba(168,181,178,0.15)] text-[hsl(var(--muted-foreground))]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`/essays/${essay.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]" onClick={() => handleEdit(essay)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--silver))] hover:text-red-400" onClick={() => handleDelete(essay._id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminShell>
  );
}
